import { ActionIcon, Avatar, Box, Button, Card, Grid, Group, Image, LoadingOverlay, ScrollArea, Select, SimpleGrid, Stack, Text, TextInput, Textarea, useMantineTheme } from "@mantine/core";
import Modal, { Props as ModalProps } from "./Modal";
import { useForm, zodResolver } from "@mantine/form";
import { useEffect, useState } from "react";
import { client } from "../../lib/axiosClient";
import FormData from "form-data";
import { IconCheck, IconSearch, IconX } from "@tabler/icons-react";
import { notifications } from "@mantine/notifications";

import Cookies from "universal-cookie";
import { z } from "zod";
import { useTranslation } from "react-i18next";

const cookies = new Cookies(null, { path: '/' });
type Props = {
    setSelectedData?: (id: string) => void;
    data?: any;
    allShipping?: any;
    refetchData?: () => void;
} & ModalProps;

export const UpdateShipping = (props: Props) => {
    const { t } = useTranslation();
    const theme = useMantineTheme();

    const schema = z.object({
        name: z.string({ message: t('modals.updateShipping.schemaName') }).min(1, { message: t('modals.updateShipping.schemaName2') }),
    });
    const [loading, setLoading] = useState(false);
    const [loadingCompanies, setLoadingCompanies] = useState(false);
    const [availableCompanies, setAvailableCompanies] = useState<any[]>([]);
    const [wilayas, setWilayas] = useState<any[]>([]);
    const [searchQuery, setSearchQuery] = useState("");

    const { onSubmit, reset, getInputProps, setValues, values } = useForm({
        initialValues: {
            name: "", apiKey: "", apiToken: ""
        },
        validate: zodResolver(schema),
        validateInputOnBlur: true,
        validateInputOnChange: true
    });

    useEffect(() => {
        if (props.opened) {
            fetchAvailableCompanies();
            fetchWilayas();
        }
    }, [props.opened]);

    useEffect(() => {
        if (props.opened && "data" in props && props.data !== null) {
            const delivery = props.data
            let newValues = {
                ...delivery,
                name: delivery?.name,
            }

            if (wilayas.length > 0) {
                Object.keys(newValues).forEach(key => {
                    if (key.toLowerCase().includes('wilaya') && typeof newValues[key] === 'object' && newValues[key]?.code) {
                        const foundWilaya = wilayas.find(w => w.code == newValues[key].code);
                        if (foundWilaya) {
                            newValues[key] = foundWilaya._id;
                        }
                    }
                });
            }
            setValues(newValues);
        }
    }, [props.data, props.opened, wilayas])

    const fetchAvailableCompanies = () => {
        setLoadingCompanies(true);
        client.get('/available-delivery-company')
            .then(({ data }) => {
                setAvailableCompanies(data);
                setLoadingCompanies(false);
            })
            .catch((err) => {
                console.error(err);
                setLoadingCompanies(false);
                notifications.show({ message: "Failed to fetch delivery companies", color: 'red' });
            });
    }

    const fetchWilayas = () => {
        client.get('/wilayas')
            .then(({ data }) => {
                setWilayas(data);
            })
            .catch((err) => {
                console.error(err);
                notifications.show({ message: "Failed to fetch wilayas", color: 'red' });
            });
    }

    const onSubmitForm = (values: any) => {
        setLoading(true)
        const selectedCompany = availableCompanies.find(c => c.name === values.name);

        let formData = {
            ...values,
            logo: selectedCompany?.logo || props.data?.logo || ""
        };

        Object.keys(formData).forEach(key => {
            if (key.toLowerCase().includes('wilaya') && typeof formData[key] === 'string') {
                const foundWilaya = wilayas.find(w => w._id === formData[key]);
                if (foundWilaya) {
                    formData[key] = { name: foundWilaya.name, code: foundWilaya.code };
                }
            }
        });

        client.put(`/delivery-company/${props.data._id}`, formData, {
            headers: {
                'Accept': 'application/json',
                'Authorization': cookies.get('token') || ""
            }
        })
            .then(({ data }) => {
                notifications.show({ message: t('modals.updateShipping.alert01'), color: '#fff' });
                console.log(data);
                setLoading(false)
                typeof props?.refetchData == "function" && props?.refetchData()
                closeModal()
            })
            .catch((error) => {
                notifications.show({ message: t('modals.updateShipping.alert02'), color: '#fff' });
                console.log(error)
                setLoading(false)
            });
    }

    const closeModal = () => {
        reset();
        props.onClose();
        setLoading(false)
    };

    const filteredCompanies = availableCompanies.filter(c =>
        c.name.toLowerCase().includes(searchQuery.toLowerCase())
    );

    return (
        <Modal
            {...props} onClose={closeModal} loading={loading}
            title={t('shippingPage.updateShipping')}
            size="lg"
            footer={
                <Box py={16} px={20} bg="slate.0">
                    <Group justify={"flex-end"} gap={"xs"}>
                        <Button color={'gray'} variant="outline" rightSection={<IconX size={15} />} bg="white" onClick={closeModal}>{t('modals.cancelling')}</Button>
                        <Button color={'#323232'} rightSection={<IconCheck size={15} />} type="submit" form="submit_form_update_shipping">{t('modals.confirmation')}</Button>
                    </Group>
                </Box>
            }
        >
            <Box style={{ padding: 20, minHeight: 400, position: 'relative' }}>
                <LoadingOverlay visible={loadingCompanies} />
                <form onSubmit={onSubmit(onSubmitForm)} id="submit_form_update_shipping">
                    <Stack gap="md">
                        {!values.name ? (
                            <>
                                <TextInput
                                    placeholder="Search delivery company..."
                                    leftSection={<IconSearch size={16} />}
                                    value={searchQuery}
                                    onChange={(e) => setSearchQuery(e.currentTarget.value)}
                                />
                                <Text size="sm" c="dimmed">Select a delivery company to switch to (Current: {props.data?.name}):</Text>
                                <ScrollArea h={350}>
                                    <SimpleGrid cols={{ base: 2, sm: 3 }} spacing="sm">
                                        {filteredCompanies.map((company) => {
                                            const isAdded = props.allShipping?.some((s: any) => s.name === company.name && s.name !== props.data?.name); // Don't disable current company
                                            return (
                                                <Card
                                                    key={company._id || company.name}
                                                    padding="sm"
                                                    radius="md"
                                                    withBorder
                                                    style={{
                                                        cursor: isAdded ? 'not-allowed' : 'pointer',
                                                        opacity: isAdded ? 0.6 : 1,
                                                        borderColor: values.name === company.name ? theme.colors.blue[6] : undefined,
                                                        backgroundColor: values.name === company.name ? theme.colors.blue[0] : undefined
                                                    }}
                                                    onClick={() => !isAdded && setValues({ ...values, name: company.name })}
                                                >
                                                    <Stack align="center" gap="xs">
                                                        <Image
                                                            src={company.logo ? `${process.env.REACT_APP_API_URL_IMAGES}/${company.logo}` : "/no-image.png"}
                                                            h={40}
                                                            w="auto"
                                                            fit="contain"
                                                        />
                                                        <Text size="xs" fw={500} ta="center">{company.name}</Text>
                                                        {isAdded && <Text size="10px" c="red">Already Added</Text>}
                                                    </Stack>
                                                </Card>
                                            );
                                        })}
                                    </SimpleGrid>
                                </ScrollArea>
                            </>
                        ) : (
                            <Stack>
                                <Group justify="space-between" align="center" mb="xs">
                                    <Group>
                                        <Avatar
                                            src={`${process.env.REACT_APP_API_URL_IMAGES}/${availableCompanies.find(c => c.name === values.name)?.logo}`}
                                            size="md"
                                        />
                                        <Text fw={700} size="lg" tt="capitalize">{values.name}</Text>
                                    </Group>
                                    <Button variant="subtle" size="xs" onClick={() => setValues({ ...values, name: "" })}>Change Company</Button>
                                </Group>

                                {availableCompanies.find(c => c.name === values.name)?.fields?.map((field: string, index: number) => {
                                    const label = field.replace(/([A-Z])/g, ' $1').replace(/^./, (str) => str.toUpperCase());

                                    if (field.toLowerCase().includes("wilaya")) {
                                        return (
                                            <Select
                                                key={index}
                                                label={label}
                                                placeholder={`Select ${label}`}
                                                withAsterisk
                                                searchable
                                                data={wilayas.map((w: any) => ({ value: w._id, label: `${w.code} - ${w.name}` }))}
                                                {...getInputProps(field)}
                                            />
                                        )
                                    }

                                    if (["apiToken", "token"].includes(field)) {
                                        return (
                                            <Textarea
                                                key={index}
                                                label={label}
                                                placeholder={`Enter ${label}`}
                                                withAsterisk
                                                rows={4}
                                                {...getInputProps(field)}
                                            />
                                        )
                                    }
                                    return (
                                        <TextInput
                                            key={index}
                                            label={label}
                                            placeholder={`Enter ${label}`}
                                            withAsterisk
                                            {...getInputProps(field)}
                                        />
                                    )
                                })}

                            </Stack>
                        )}
                    </Stack>
                </form>
            </Box>
        </Modal>
    );
};